import { Injectable } from "@angular/core";
import { BehaviorSubject } from "rxjs";
import { NavigationService } from "../../common/navigation/navigation.service";
import { LogoutState } from "app/models";

@Injectable()
export class RegistrationService {
	private registeredSubject = new BehaviorSubject<string | undefined>(undefined);
	private registrationConfirmedSubject = new BehaviorSubject<string | undefined>(undefined);
	private loaderVisibleSubject = new BehaviorSubject<boolean>(false);

	constructor(private navigationService: NavigationService) { }

	/**
	 * Should be called when registration is confirmed successfully. The intended use is to provide the user details to the login page.
	 *
	 * @param email the email address of the user that just completed registration
	 */
	public submitRegistrationConfirmation(email: string) {
		this.registrationConfirmedSubject.next(email);
	}

	/**
	 * The subject that provides post-registration email addresses.
	 */
	public get registrationConfirmed() {
		return this.registrationConfirmedSubject.asObservable();
	}

	/**
	 * Should be called when we want to show or hide the loading bar on the login and registration area.
	 *
	 * @param visible
	 */
	public submitLoaderVisible(visible: boolean) {
		this.loaderVisibleSubject.next(visible);
	}

	/**
	 * The subject that provides the visibility state of the loading bar.
	 */
	public get loaderVisible() {
		return this.loaderVisibleSubject.asObservable();
	}

	/**
	 * @return true if the loader is supposed to be visible
	 */
	public get isLoaderVisible() {
		return this.loaderVisibleSubject.value;
	}

	/**
	 * Helper method for navigating to the subroute on the login and registration page. This shows the main login prompt.
	 */
	public navigateToLogin(state?: LogoutState) {
		if (!!state) {
			this.navigationService.navigate(
				[
					"/login/login"
				],
				{ state: state }
			);
		} else {
			this.navigationService.navigate(
				[
					"/login/login"
				]
			);
		}
	}

	/**
	 * Helper method for navigating to the subroute on the login and
	 * registration page. This shows the main login prompt, with the messaging
	 * that verification was successful.
	 */
	public navigateToLoginVerify() {
		this.navigationService.navigate(
			[
				"/login/loginVerify"
			]
		);
	}

	/**
	 * Helper method for navigating to the subroute on the login and registration page. This shows the registration prompt.
	 */
	public navigateToRegister() {
		this.navigationService.navigate(
			[
				"/login/register"
			]
		);
	}

	/**
	 * Helper method for navigating to the subroute on the login and registration page. This shows the registration code verification prompt.
	 */
	public navigateToRegisterConfirm() {
		this.navigationService.navigate(
			[
				"/login/registerConfirm"
			]
		);
	}
}
